import { useId } from 'react'
import PropTypes from 'prop-types'

/** The bordered control: 48px, 8px corners, rule-blue focus ring. */
export const fieldClass =
  'block w-full min-w-0 h-pitch-2 px-3 bg-sheet border border-ink-line rounded-lg font-sans text-ink placeholder:text-ink-mute transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-rule-blue focus:ring-[3px] focus:ring-rule-blue/15 disabled:bg-ink-wash disabled:text-ink-mute'

/**
 * The compact labelled control for dense editors: a bordered input with
 * its label above. `as="select"` (options as children) or
 * `as="textarea"`. `error` prints under the field in rule red and marks
 * the border; otherwise `hint` prints in mute ink.
 */
export default function Field({
  label,
  hint,
  error,
  as: Tag = 'input',
  id: idProp,
  className = '',
  inputClassName = '',
  children,
  ...rest
}) {
  const autoId = useId()
  const id = idProp || autoId
  const border = error ? 'border-rule-red' : ''
  const describedBy = error || hint ? `${id}-note` : undefined
  const shared = {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': rest['aria-describedby'] || describedBy,
  }
  let control
  if (Tag === 'select') {
    control = (
      <div className="relative">
        <select
          {...rest}
          {...shared}
          className={`${fieldClass} appearance-none pr-8 truncate ${border} ${inputClassName}`}
        >
          {children}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 12 8"
          className="pointer-events-none absolute right-3 top-1/2 h-2 w-3 -translate-y-1/2 text-ink-mute"
        >
          <path
            d="M1 1l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    )
  } else if (Tag === 'textarea') {
    control = (
      <textarea
        {...rest}
        {...shared}
        className={`${fieldClass} h-auto min-h-[96px] py-3 leading-pitch ${border} ${inputClassName}`}
      />
    )
  } else {
    control = (
      <input
        {...rest}
        {...shared}
        className={`${fieldClass} ${border} ${inputClassName}`}
      />
    )
  }
  return (
    <div className={`min-w-0 ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className="block mb-1.5 font-sans text-sm font-medium text-ink-soft"
        >
          {label}
        </label>
      )}
      {control}
      {error ? (
        <p
          id={describedBy}
          className="mt-1 font-sans text-sm text-rule-red leading-5"
        >
          {error}
        </p>
      ) : hint ? (
        <p
          id={describedBy}
          className="mt-1 font-sans text-sm text-ink-mute leading-5"
        >
          {hint}
        </p>
      ) : null}
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.node,
  hint: PropTypes.node,
  error: PropTypes.node,
  as: PropTypes.oneOf(['input', 'select', 'textarea']),
  id: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  children: PropTypes.node,
}
